import { createContext, FunctionComponent, useContext, useEffect, useState } from 'react';

// Contexts
import { useAlertContext } from '@Components/Alert/Context';
import { getHttpErrorMessage, useHttpContext } from '@Libs/http';

// api
import diseaseApi, { DiseasePrams } from '@Libs/api/cms/disease';

export interface DiseaseItem {
    id: string;
    slug: string;
    title: string;
    [key: string]: any;
}

interface DiseasesContextType {
    total: number;
    hasMore: boolean;
    keyword: string;
    diseases: Array<DiseaseItem>;
    loadMore: (queries: DiseasePrams) => void;
}

const initState: DiseasesContextType = {
    total: 0,
    keyword: '',
    diseases: [],
    hasMore: false,
    loadMore: () => null,
};

const DiseasesContext = createContext<DiseasesContextType>(initState);

export const useDiseasesManager = (): DiseasesContextType => useContext(DiseasesContext);

interface ProviderProps {
    page: number;
    limit: number;
    total: number;
    keyword?: string;
    alphabet?: string;
    diseases: Array<DiseaseItem>;
}

export const Provider: FunctionComponent<ProviderProps> = ({ page, keyword, alphabet, total, ...props }) => {
    const { alert } = useAlertContext();
    const { cmsHttp } = useHttpContext();

    const [diseases, setDiseases] = useState<Array<DiseaseItem>>(props.diseases || []);
    const [hasMore, setHasMore] = useState<boolean>(props.diseases.length >= props.limit);

    useEffect(() => {
        setDiseases(props.diseases);
        setHasMore(props.diseases.length >= props.limit);
    }, [page, keyword, alphabet]);

    const loadMore = async (queries: DiseasePrams) => {
        if (hasMore) {
            try {
                let diseases = [];

                if (alphabet) {
                    const responseDiseases = await diseaseApi(cmsHttp).findOneDiseaseByAlphabet(alphabet, queries);

                    diseases = responseDiseases.diseases;
                } else {
                    diseases = await diseaseApi(cmsHttp).find(queries);
                }

                setHasMore(diseases.length >= props.limit);
                setDiseases((currentDiseases) => [...currentDiseases, ...diseases]);
            } catch (error) {
                alert({
                    type: 'error',
                    title: 'Đã xảy ra lỗi',
                    text: getHttpErrorMessage(error),
                });
            }
        }
    };

    return <DiseasesContext.Provider value={{ total, hasMore, keyword, diseases, loadMore }}>{props.children}</DiseasesContext.Provider>;
};

Provider.defaultProps = {
    diseases: [],
};

type WithDiseasesManager<T> = T & ProviderProps;

export function withDiseasesManager<P>(WrappedComponent: FunctionComponent): ({ limit }) => FunctionComponent {
    return ({ limit = 39 }): FunctionComponent<WithDiseasesManager<P>> => {
        return ({ page, keyword, total, alphabet, diseases, ...props }) => {
            return (
                <Provider page={page} keyword={keyword} total={total} alphabet={alphabet} diseases={diseases} limit={limit}>
                    <WrappedComponent keyword={keyword} {...props} />
                </Provider>
            );
        };
    };
}
